/**
 * Pendaftaran umat (section 09): the desk registration flow in the poster's order. One
 * schema of eleven steps drives the task rail, the step forms and the blank Formulir
 * Pendataan; the helpers read and write `keluarga.pendaftaran` (spec 8.3) and turn the
 * Kartu Keluarga grid into the ops of a family (keluarga, umat, anggota_keluarga).
 *
 * Dialect-neutral and imported as `@satu/shared/pendaftaran`, so the shell never carries
 * it: the route chunk, the worker and the print template are its only readers.
 */
import { formatPartialDateInput, parsePartialDateInput } from './dates/partial'
import {
  type DaftarKode,
  HUBUNGAN_DARI_SHDK,
  labelKode,
  PEKERJAAN,
  PENDIDIKAN,
  SHDK,
  STATUS_KAWIN,
} from './lookups/index'
import { HUBUNGAN, type JENIS_DOKUMEN, STATUS_PERKAWINAN } from './schema/contract'
import ui from './strings/ui.id.json' with { type: 'json' }
import { type LocalWrite, uuidv7 } from './sync/ops'
import {
  type KEADAAN_LANGKAH,
  LANGKAH_PENDAFTARAN,
  type Pendaftaran,
  pendaftaranBaru,
  pendaftaranSchema,
  type ReferensiSakramenLuar,
} from './validators/kolom'

const teks = ui.pendaftaran

export type LangkahId = (typeof LANGKAH_PENDAFTARAN)[number]
export type KeadaanLangkah = (typeof KEADAAN_LANGKAH)[number]
export type TabelSakramen = 'baptis' | 'komuni_pertama' | 'krisma' | 'perkawinan'
/** The keys of `umat.referensi_sakramen_luar`. */
export type KunciSakramen = keyof ReferensiSakramenLuar
export type JenisDokumen = (typeof JENIS_DOKUMEN)[number]
export type Hubungan = (typeof HUBUNGAN)[number]
export type StatusPerkawinan = (typeof STATUS_PERKAWINAN)[number]

// ---------------------------------------------------------------------------
// The step schema
// ---------------------------------------------------------------------------

export type JenisIsian = 'teks' | 'angka' | 'tanggal' | 'pilihan' | 'radio' | 'pindai' | 'daftar'
export type DaftarIsian =
  | 'pendidikan'
  | 'pekerjaan'
  | 'statusKawin'
  | 'hubungan'
  | 'lingkungan'
  | 'paroki'

export interface IsianPendaftaran {
  readonly id: string
  readonly label: string
  readonly jenis: JenisIsian
  readonly opsional?: true
  /** Which device list a `pilihan` field offers. */
  readonly daftar?: DaftarIsian
  /** Fixed options of a `radio` field. */
  readonly opsi?: readonly { readonly value: string; readonly label: string }[]
  /** The scanned document type of a `pindai` field. */
  readonly dokumen?: JenisDokumen
  /** Print: how much of a line the blank takes. */
  readonly lebar?: 'penuh' | 'setengah' | 'pendek'
}

export interface StepDef {
  readonly id: LangkahId
  readonly nomor: number
  readonly label: string
  /** The item can be absent for a family: the third rail state, "Tidak ada". */
  readonly optional: boolean
  /** The step walks the members with the ‹ › switcher. */
  readonly perMember: boolean
  readonly sakramen?: TabelSakramen
  /** The document type this step scans. */
  readonly dokumen?: JenisDokumen
  readonly isian: readonly IsianPendaftaran[]
}

const OPSI_LP = [
  { value: 'L', label: ui.enum.jenisKelamin.L },
  { value: 'P', label: ui.enum.jenisKelamin.P },
] as const

/** A list's options in its words, in the list's order. */
function opsiDari(label: Readonly<Record<string, string>>): { value: string; label: string }[] {
  return Object.entries(label).map(([value, l]) => ({ value, label: l }))
}

/** The six fields of a certificate reference (steps 5 to 8, one block per sacrament on paper). */
export const ISIAN_RUJUKAN: readonly IsianPendaftaran[] = [
  { id: 'paroki', label: teks.sakramen.paroki, jenis: 'pilihan', daftar: 'paroki', lebar: 'penuh' },
  { id: 'buku', label: teks.sakramen.buku, jenis: 'teks', lebar: 'pendek' },
  { id: 'halaman', label: teks.sakramen.halaman, jenis: 'angka', lebar: 'pendek' },
  { id: 'nomor', label: teks.sakramen.nomor, jenis: 'teks', lebar: 'pendek' },
  { id: 'tanggal', label: teks.sakramen.tanggal, jenis: 'tanggal', lebar: 'setengah' },
  { id: 'tempat', label: teks.sakramen.tempat, jenis: 'teks', lebar: 'setengah' },
]

/** The KTP fields: step 2 for the head, repeated per other member in "Anggota lain". */
export const ISIAN_KTP: readonly IsianPendaftaran[] = [
  { id: 'nik', label: teks.ktp.nik, jenis: 'angka', lebar: 'setengah' },
  { id: 'namaLengkap', label: teks.ktp.namaLengkap, jenis: 'teks', lebar: 'penuh' },
  { id: 'tempatLahir', label: teks.ktp.tempatLahir, jenis: 'teks', lebar: 'setengah' },
  { id: 'tanggalLahir', label: teks.ktp.tanggalLahir, jenis: 'tanggal', lebar: 'setengah' },
  { id: 'jenisKelamin', label: teks.ktp.jenisKelamin, jenis: 'radio', opsi: OPSI_LP },
  { id: 'pindaiKtp', label: teks.ktp.pindaiKtp, jenis: 'pindai', dokumen: 'ktp' },
]

const sakramenIsian = (dokumen: JenisDokumen): IsianPendaftaran[] => [
  ...ISIAN_RUJUKAN,
  { id: 'pindaiSurat', label: teks.sakramen.pindaiSurat, jenis: 'pindai', dokumen },
]

export const LANGKAH: readonly StepDef[] = [
  {
    id: 'kk',
    nomor: 1,
    label: teks.langkah.kk,
    optional: false,
    perMember: false,
    dokumen: 'kk',
    isian: [
      {
        id: 'nomorKkPemerintah',
        label: teks.kk.nomorKkPemerintah,
        jenis: 'angka',
        lebar: 'setengah',
      },
      // Release 18: the parish number is generated, never written in; the family's own code is.
      { id: 'kodeKeluarga', label: teks.kk.kodeKeluarga, jenis: 'teks', lebar: 'setengah' },
      { id: 'alamat', label: teks.kk.alamat, jenis: 'teks', lebar: 'penuh' },
      { id: 'rtRw', label: teks.kk.rtRw, jenis: 'teks', lebar: 'pendek' },
      { id: 'kampung', label: teks.kk.kampung, jenis: 'teks', lebar: 'setengah' },
      { id: 'distrik', label: teks.kk.distrik, jenis: 'teks', lebar: 'setengah' },
      // Every field the screen's KK step asks is on the paper form too (test sweep 2026-10-04, A29).
      { id: 'kabupatenKota', label: teks.kk.kabupatenKota, jenis: 'teks', lebar: 'setengah' },
      { id: 'provinsi', label: teks.kk.provinsi, jenis: 'teks', lebar: 'setengah' },
      { id: 'kodePos', label: teks.kk.kodePos, jenis: 'angka', lebar: 'pendek' },
      { id: 'tanggalKk', label: teks.kk.tanggalKk, jenis: 'tanggal', lebar: 'setengah' },
      {
        id: 'lingkungan',
        label: teks.kk.lingkungan,
        jenis: 'pilihan',
        daftar: 'lingkungan',
        lebar: 'setengah',
      },
      // Release 19: the household's housing (`keluarga.status_rumah`).
      {
        id: 'statusRumah',
        label: ui.keluarga.umat.kolom.statusRumah,
        jenis: 'radio',
        opsional: true,
        opsi: opsiDari(ui.enum.statusRumah),
        lebar: 'penuh',
      },
      { id: 'pindaiKk', label: teks.kk.pindaiKk, jenis: 'pindai', dokumen: 'kk' },
    ],
  },
  {
    id: 'ktp',
    nomor: 2,
    label: teks.langkah.ktp,
    optional: false,
    perMember: false,
    dokumen: 'ktp',
    isian: ISIAN_KTP,
  },
  {
    id: 'hp',
    nomor: 3,
    label: teks.langkah.hp,
    optional: false,
    perMember: false,
    isian: [{ id: 'nomorHp', label: teks.hp.nomorHp, jenis: 'teks', lebar: 'setengah' }],
  },
  {
    id: 'email',
    nomor: 4,
    label: teks.langkah.email,
    optional: true,
    perMember: false,
    isian: [
      { id: 'email', label: teks.email.email, jenis: 'teks', opsional: true, lebar: 'setengah' },
    ],
  },
  {
    id: 'baptis',
    nomor: 5,
    label: teks.langkah.baptis,
    optional: false,
    perMember: true,
    sakramen: 'baptis',
    dokumen: 'surat_baptis',
    isian: sakramenIsian('surat_baptis'),
  },
  {
    id: 'komuni',
    nomor: 6,
    label: teks.langkah.komuni,
    optional: false,
    perMember: true,
    sakramen: 'komuni_pertama',
    dokumen: 'surat_komuni',
    isian: sakramenIsian('surat_komuni'),
  },
  {
    id: 'krisma',
    nomor: 7,
    label: teks.langkah.krisma,
    optional: false,
    perMember: true,
    sakramen: 'krisma',
    dokumen: 'surat_krisma',
    isian: sakramenIsian('surat_krisma'),
  },
  {
    id: 'perkawinan',
    nomor: 8,
    label: teks.langkah.perkawinan,
    optional: true,
    perMember: true,
    sakramen: 'perkawinan',
    dokumen: 'surat_perkawinan_gereja',
    isian: [
      ...sakramenIsian('surat_perkawinan_gereja'),
      {
        id: 'statusPerkawinan',
        label: teks.sakramen.statusPerkawinan,
        jenis: 'radio',
        opsi: [
          { value: 'menikah_gereja', label: ui.enum.statusPerkawinan.menikah_gereja },
          { value: 'menikah_belum_gereja', label: ui.enum.statusPerkawinan.menikah_belum_gereja },
        ],
      },
      {
        id: 'aktaNikahSipil',
        label: teks.sakramen.aktaNikahSipil,
        jenis: 'pindai',
        opsional: true,
        dokumen: 'akta_nikah_sipil',
      },
    ],
  },
  {
    id: 'pendidikan',
    nomor: 9,
    label: teks.langkah.pendidikan,
    optional: true,
    perMember: true,
    dokumen: 'ijazah',
    isian: [
      {
        id: 'pendidikanTerakhir',
        label: teks.pendidikan.pendidikanTerakhir,
        jenis: 'pilihan',
        daftar: 'pendidikan',
        lebar: 'penuh',
      },
      {
        id: 'ijazah',
        label: teks.pendidikan.ijazah,
        jenis: 'pindai',
        opsional: true,
        dokumen: 'ijazah',
      },
    ],
  },
  {
    id: 'pekerjaan',
    nomor: 10,
    label: teks.langkah.pekerjaan,
    optional: false,
    perMember: true,
    isian: [
      {
        id: 'pekerjaan',
        label: teks.pekerjaan.pekerjaan,
        jenis: 'pilihan',
        daftar: 'pekerjaan',
        lebar: 'penuh',
      },
      { id: 'keterampilan', label: teks.pekerjaan.keterampilan, jenis: 'daftar', lebar: 'penuh' },
    ],
  },
  {
    id: 'anggota',
    nomor: 11,
    label: teks.langkah.anggota,
    optional: true,
    perMember: true,
    dokumen: 'ktp',
    isian: [
      ...ISIAN_KTP.filter((f) => f.id !== 'pindaiKtp'),
      {
        id: 'hubungan',
        label: teks.anggotaLain.hubungan,
        jenis: 'pilihan',
        daftar: 'hubungan',
        lebar: 'setengah',
      },
      { id: 'pindaiKtp', label: teks.ktp.pindaiKtp, jenis: 'pindai', dokumen: 'ktp' },
    ],
  },
]

export const JUMLAH_LANGKAH = LANGKAH.length

const BY_ID = new Map<string, StepDef>(LANGKAH.map((l) => [l.id, l]))

export function isLangkahId(value: unknown): value is LangkahId {
  return typeof value === 'string' && BY_ID.has(value)
}

export function langkahDari(id: unknown): StepDef | null {
  return isLangkahId(id) ? (BY_ID.get(id) ?? null) : null
}

export function langkahSesudah(id: LangkahId): StepDef | null {
  const index = LANGKAH.findIndex((l) => l.id === id)
  return LANGKAH[index + 1] ?? null
}

export function langkahSebelum(id: LangkahId): StepDef | null {
  const index = LANGKAH.findIndex((l) => l.id === id)
  return index > 0 ? (LANGKAH[index - 1] ?? null) : null
}

/** "2. KTP kepala keluarga" */
export function judulLangkah(step: StepDef): string {
  return teks.judulLangkah.replace('{nomor}', String(step.nomor)).replace('{label}', step.label)
}

// ---------------------------------------------------------------------------
// The Kartu Keluarga grid, in Dukcapil column order
// ---------------------------------------------------------------------------

export type KolomAnggotaId =
  | 'no'
  | 'namaLengkap'
  | 'nik'
  | 'ttl'
  | 'jenisKelamin'
  | 'pendidikan'
  | 'pekerjaan'
  | 'statusKawin'
  | 'hubungan'

export interface KolomAnggota {
  readonly id: KolomAnggotaId
  readonly label: string
  readonly jenis: 'nomor' | 'teks' | 'angka' | 'ttl' | 'radio' | 'pilihan'
  readonly daftar?: DaftarIsian
  /** CSS width of the column on a laptop. */
  readonly lebar: string
}

const k = teks.kk.kolom
export const KOLOM_ANGGOTA_KK: readonly KolomAnggota[] = [
  { id: 'no', label: k.no, jenis: 'nomor', lebar: '2.25rem' },
  { id: 'namaLengkap', label: k.namaLengkap, jenis: 'teks', lebar: '12rem' },
  { id: 'nik', label: k.nik, jenis: 'angka', lebar: '9.5rem' },
  { id: 'ttl', label: k.ttl, jenis: 'ttl', lebar: '9.5rem' },
  { id: 'jenisKelamin', label: k.jenisKelamin, jenis: 'radio', lebar: '3.5rem' },
  { id: 'pendidikan', label: k.pendidikan, jenis: 'pilihan', daftar: 'pendidikan', lebar: '8rem' },
  { id: 'pekerjaan', label: k.pekerjaan, jenis: 'pilihan', daftar: 'pekerjaan', lebar: '9rem' },
  {
    id: 'statusKawin',
    label: k.statusKawin,
    jenis: 'pilihan',
    daftar: 'statusKawin',
    lebar: '8.5rem',
  },
  { id: 'hubungan', label: k.hubungan, jenis: 'pilihan', daftar: 'hubungan', lebar: '8.5rem' },
]

// ---------------------------------------------------------------------------
// keluarga.pendaftaran
// ---------------------------------------------------------------------------

/**
 * The stored JSON (text or object) as a full `Pendaftaran`. A family that never went
 * through the desk flow (import, books, an older device) has no JSON: every step reads
 * "Belum" and `selesai` is true, because no registration is in progress for it; touching
 * it through the flow must not turn the list chip on.
 */
export function bacaPendaftaran(value: unknown): Pendaftaran {
  let raw: unknown = value
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw) as unknown
    } catch {
      raw = null
    }
  }
  if (raw === null || raw === undefined) return { ...pendaftaranBaru(), selesai: true }
  const parsed = pendaftaranSchema.safeParse(raw)
  if (parsed.success) return parsed.data
  // A partial object (older rows): known steps kept, the rest "belum".
  const dasar = pendaftaranBaru()
  const obj = raw as { selesai?: unknown; langkah?: Record<string, unknown> }
  const langkah = { ...dasar.langkah }
  for (const id of LANGKAH_PENDAFTARAN) {
    const v = obj.langkah?.[id]
    if (v === 'selesai' || v === 'belum' || v === 'tidak_ada') langkah[id] = v
  }
  return { selesai: obj.selesai === true, langkah }
}

export function keadaanLangkah(p: Pendaftaran, id: LangkahId): KeadaanLangkah {
  return p.langkah[id]
}

/** Every step done or recorded as absent. */
export function semuaSelesai(p: Pendaftaran): boolean {
  return LANGKAH_PENDAFTARAN.every((id) => p.langkah[id] !== 'belum')
}

/** Steps that are not "Belum": the footer's "1 dari 11". */
export function jumlahSelesai(p: Pendaftaran): number {
  return LANGKAH_PENDAFTARAN.filter((id) => p.langkah[id] !== 'belum').length
}

/**
 * Record one step. `selesai` never goes back to false: a registration the desk (or the
 * import) closed stays closed; a flow-created family closes when every step is done or
 * when the desk presses Selesai (`tutup`).
 */
export function tandaiLangkah(
  p: Pendaftaran,
  id: LangkahId,
  keadaan: KeadaanLangkah,
  options: { tutup?: boolean | undefined } = {},
): Pendaftaran {
  const langkah = { ...p.langkah, [id]: keadaan }
  // Every other key (the kept answers) stays.
  const next = { ...p, langkah }
  return { ...next, selesai: options.tutup === true || p.selesai || semuaSelesai(next) }
}

/** The first step still "Belum", or null when none is. */
export function langkahBerikutnya(p: Pendaftaran): StepDef | null {
  for (const step of LANGKAH) if (p.langkah[step.id] === 'belum') return step
  return null
}

// ---------------------------------------------------------------------------
// TTL: "Agats, 12-05-1987" in one cell, as the KK prints it
// ---------------------------------------------------------------------------

export interface Ttl {
  tempat: string | null
  /** Canonical partial date. */
  tanggal: string | null
  /** False when a date was typed but does not read. */
  valid: boolean
}

export function pisahTtl(text: string): Ttl {
  const t = text.replace(/\s+/g, ' ').trim()
  if (t === '') return { tempat: null, tanggal: null, valid: true }
  const koma = t.lastIndexOf(',')
  if (koma === -1) {
    const tanggal = parsePartialDateInput(t)
    if (tanggal !== null) return { tempat: null, tanggal, valid: true }
    // Digits and separators only: a date that does not read, not a place.
    if (/^[\d\s./-]+$/.test(t)) return { tempat: null, tanggal: null, valid: false }
    return { tempat: t, tanggal: null, valid: true }
  }
  const tempat = t.slice(0, koma).trim()
  const sisa = t.slice(koma + 1).trim()
  const tanggal = sisa === '' ? null : parsePartialDateInput(sisa)
  return {
    tempat: tempat === '' ? null : tempat,
    tanggal,
    valid: sisa === '' || tanggal !== null,
  }
}

/** The one cell back from the two columns: "Agats, 12-05-1987", "Agats", "1987" or "". */
export function gabungTtl(
  tempat: string | null | undefined,
  tanggal: string | null | undefined,
): string {
  const t = tempat === null || tempat === undefined ? '' : tempat.trim()
  const d = formatPartialDateInput(tanggal)
  if (t === '') return d
  if (d === '') return t
  return `${t}, ${d}`
}

// ---------------------------------------------------------------------------
// Labels the desk types, codes the row stores
// ---------------------------------------------------------------------------

const kunci = (text: string) =>
  text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s*†\s*$/, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

/** The labels of a code list as the comboboxes offer them (Church additions marked †). */
export function labelDaftar(daftar: DaftarKode): string[] {
  return daftar.entri.map((e) => (e.tambahanGereja === true ? `${e.label} †` : e.label))
}

/** The code behind a typed label, or null when the text is not in the list. */
export function kodeDariLabel(daftar: DaftarKode, label: string): string | null {
  const key = kunci(label)
  if (key === '') return null
  return (
    daftar.entri.find((e) => kunci(e.label) === key || kunci(e.labelResmi ?? '') === key)?.kode ??
    null
  )
}

export function labelDariKode(daftar: DaftarKode, kode: string | null | undefined): string {
  if (kode === null || kode === undefined || kode === '') return ''
  const entri = daftar.entri.find((e) => e.kode === kode)
  if (entri === undefined) return kode
  return entri.tambahanGereja === true ? `${entri.label} †` : entri.label
}

export const LABEL_HUBUNGAN: readonly string[] = HUBUNGAN.map((h) => ui.enum.hubungan[h])
/**
 * What the KK grid offers a member other than the head (owner, 2026-10-04; A10, A3): only the
 * roles SATU stores, in the Church words. The head's row shows "Kepala keluarga" itself, and
 * Mertua, Menantu, Cucu, Pembantu or Lainnya on a government card are "Famili lain" here: a role
 * the record cannot keep is not offered. A typed SHDK word still reads (`hubunganDariLabel`).
 */
export const LABEL_HUBUNGAN_KK: readonly string[] = HUBUNGAN.filter((h) => h !== 'kepala').map(
  (h) => ui.enum.hubungan[h],
)
export const LABEL_STATUS_KAWIN: readonly string[] = STATUS_KAWIN.entri.map((e) => e.label)

/** A typed household role (an SHDK label or a Church word) to the Church enum. */
export function hubunganDariLabel(label: string): Hubungan | null {
  const key = kunci(label)
  if (key === '') return null
  const shdk = SHDK.entri.find((e) => kunci(e.label) === key)
  if (shdk !== undefined) return HUBUNGAN_DARI_SHDK[shdk.kode] ?? 'famili_lain'
  const church = HUBUNGAN.find((h) => kunci(ui.enum.hubungan[h]) === key)
  return church ?? null
}

export function labelHubungan(kode: string | null | undefined): string {
  if (kode === null || kode === undefined) return ''
  return (ui.enum.hubungan as Record<string, string>)[kode] ?? kode
}

/**
 * Dukcapil status kawin to the Church enum (the reading section 17 also uses): a civil
 * registration follows a religious marriage, so "Kawin Tercatat" reads as a Church
 * marriage; step 8 asks the question properly.
 */
export const STATUS_KAWIN_KE_PERKAWINAN: Readonly<Record<string, StatusPerkawinan>> = {
  '1': 'belum_menikah',
  '2': 'menikah_gereja',
  '3': 'menikah_belum_gereja',
  '4': 'cerai',
  '5': 'cerai',
  '6': 'janda_duda',
}

export function statusPerkawinanDariLabel(label: string): StatusPerkawinan | null {
  const kode = kodeDariLabel(STATUS_KAWIN, label)
  if (kode !== null) return STATUS_KAWIN_KE_PERKAWINAN[kode] ?? null
  const key = kunci(label)
  return STATUS_PERKAWINAN.find((s) => kunci(ui.enum.statusPerkawinan[s]) === key) ?? null
}

/** The Dukcapil label shown for a stored Church status (the grid on resume). */
export function labelStatusKawin(status: string | null | undefined): string {
  if (status === null || status === undefined) return ''
  const kode = Object.entries(STATUS_KAWIN_KE_PERKAWINAN).find(([, s]) => s === status)?.[0]
  if (kode !== undefined) return labelKode(STATUS_KAWIN, kode)
  return (ui.enum.statusPerkawinan as Record<string, string>)[status] ?? status
}

// ---------------------------------------------------------------------------
// Grid rows to umat columns
// ---------------------------------------------------------------------------

export interface BarisAnggota {
  /** The stored person when the row came from the device; null for a typed row. */
  umatId: string | null
  namaLengkap: string
  nik: string
  ttl: string
  jenisKelamin: string
  pendidikan: string
  pekerjaan: string
  statusKawin: string
  hubungan: string
}

export function barisKosong(): BarisAnggota {
  return {
    umatId: null,
    namaLengkap: '',
    nik: '',
    ttl: '',
    jenisKelamin: '',
    pendidikan: '',
    pekerjaan: '',
    statusKawin: '',
    hubungan: '',
  }
}

export interface UmatUntukBaris {
  id: string
  namaLengkap: string
  nik: string | null
  tempatLahir: string | null
  tanggalLahir: string | null
  jenisKelamin: string | null
  pendidikanKode: string | null
  pekerjaanKode: string | null
  statusPerkawinan: string | null
  hubungan: string | null
}

/** A stored person as a grid row (resume). */
export function barisDariUmat(u: UmatUntukBaris): BarisAnggota {
  return {
    umatId: u.id,
    namaLengkap: u.namaLengkap,
    nik: u.nik ?? '',
    ttl: gabungTtl(u.tempatLahir, u.tanggalLahir),
    jenisKelamin: u.jenisKelamin ?? '',
    pendidikan: labelDariKode(PENDIDIKAN, u.pendidikanKode),
    pekerjaan: labelDariKode(PEKERJAAN, u.pekerjaanKode),
    statusKawin: labelStatusKawin(u.statusPerkawinan),
    hubungan: labelHubungan(u.hubungan),
  }
}

export interface MasalahBaris {
  /** 1-based row number as the grid shows it. */
  baris: number
  kolom: KolomAnggotaId
  pesan: string
}

/** True when the row holds nothing but its number. */
export function barisKosongkah(row: BarisAnggota): boolean {
  return (
    row.namaLengkap.trim() === '' &&
    row.nik.trim() === '' &&
    row.ttl.trim() === '' &&
    row.jenisKelamin === '' &&
    row.pendidikan.trim() === '' &&
    row.pekerjaan.trim() === '' &&
    row.statusKawin.trim() === '' &&
    row.hubungan.trim() === ''
  )
}

/** The desk's checks on one row: a name, a 16-digit NIK, a readable date. */
export function periksaBaris(row: BarisAnggota, index: number): MasalahBaris[] {
  const out: MasalahBaris[] = []
  const baris = index + 1
  if (row.namaLengkap.trim() === '') {
    out.push({ baris, kolom: 'namaLengkap', pesan: ui.validasi.wajib })
  }
  const nik = row.nik.replace(/\s+/g, '')
  if (nik !== '' && !/^\d{16}$/.test(nik)) out.push({ baris, kolom: 'nik', pesan: ui.validasi.nik })
  if (!pisahTtl(row.ttl).valid) {
    out.push({ baris, kolom: 'ttl', pesan: ui.validasi.tanggalParsial })
  }
  return out
}

/** The umat columns a row supplies; `kepala` forces the household role of the first row. */
export function kolomUmatDariBaris(row: BarisAnggota, kepala: boolean): Record<string, unknown> {
  const ttl = pisahTtl(row.ttl)
  const nik = row.nik.replace(/\s+/g, '')
  return {
    nama_lengkap: row.namaLengkap.trim(),
    nik: nik === '' ? null : nik,
    tempat_lahir: ttl.tempat,
    tanggal_lahir: ttl.tanggal,
    jenis_kelamin: row.jenisKelamin === 'L' || row.jenisKelamin === 'P' ? row.jenisKelamin : null,
    pendidikan_kode: kodeDariLabel(PENDIDIKAN, row.pendidikan),
    pekerjaan_kode: kodeDariLabel(PEKERJAAN, row.pekerjaan),
    status_perkawinan: statusPerkawinanDariLabel(row.statusKawin),
    hubungan: kepala ? 'kepala' : (hubunganDariLabel(row.hubungan) ?? null),
  }
}

/** The columns whose value differs from the stored person: the op's fields (empty when none). */
export function perubahanAnggota(
  u: UmatUntukBaris,
  row: BarisAnggota,
  kepala: boolean,
): Record<string, unknown> {
  const sekarang: Record<string, unknown> = {
    nama_lengkap: u.namaLengkap,
    nik: u.nik,
    tempat_lahir: u.tempatLahir,
    tanggal_lahir: u.tanggalLahir,
    jenis_kelamin: u.jenisKelamin,
    pendidikan_kode: u.pendidikanKode,
    pekerjaan_kode: u.pekerjaanKode,
    status_perkawinan: u.statusPerkawinan,
    hubungan: u.hubungan,
  }
  const baru = kolomUmatDariBaris(row, kepala)
  const fields: Record<string, unknown> = {}
  for (const [kolom, nilai] of Object.entries(baru)) {
    // A cleared combobox (a label outside the list) never wipes a stored code.
    if (nilai === null && sekarang[kolom] !== null && kolom !== 'nik' && kolom !== 'jenis_kelamin')
      continue
    if ((sekarang[kolom] ?? null) !== (nilai ?? null)) fields[kolom] = nilai
  }
  return fields
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface RumahTangga {
  parokiId: string | null
  lingkunganId: string
  nomorKkKatolik: string
  nomorKkPemerintah: string | null
  /** Kode keluarga (schema 14), stored trimmed; null for none. */
  kodeKeluarga?: string | null
  alamat: string | null
  rtRw: string | null
  kampung: string | null
  distrik: string | null
  /** From the Kartu Keluarga (Dokumen jadi teks, 2026-09-30). */
  kabupatenKota?: string | null
  provinsi?: string | null
  kodePos?: string | null
  /** Canonical partial date. */
  tanggalKk?: string | null
  /** Status rumah tinggal (schema 15), a `STATUS_RUMAH` code; null for none. */
  statusRumah?: string | null
}

export interface AnggotaBaruInput {
  keluargaId: string
  parokiId: string | null
  lingkunganId: string
  row: BarisAnggota
  kepala: boolean
  /** The device's "today" in WIT, the membership's `mulai`. */
  hariIni: string
  newId?: () => string
}

/** One person joining the household: the umat row and the open membership row. */
export function tulisanAnggotaBaru(input: AnggotaBaruInput): {
  writes: LocalWrite[]
  umatId: string
} {
  const newId = input.newId ?? uuidv7
  const umatId = newId()
  const writes: LocalWrite[] = [
    {
      table: 'umat',
      row_id: umatId,
      fields: {
        paroki_id: input.parokiId,
        lingkungan_id: input.lingkunganId,
        keluarga_id: input.keluargaId,
        status_gereja: 'katolik',
        status_hidup: 'hidup',
        ...kolomUmatDariBaris(input.row, input.kepala),
      },
    },
    {
      table: 'anggota_keluarga',
      row_id: newId(),
      fields: {
        paroki_id: input.parokiId,
        lingkungan_id: input.lingkunganId,
        umat_id: umatId,
        keluarga_id: input.keluargaId,
        hubungan: input.kepala
          ? 'kepala'
          : (hubunganDariLabel(input.row.hubungan) ?? 'famili_lain'),
        mulai: input.hariIni,
        selesai: null,
        alasan_selesai: null,
      },
    },
  ]
  return { writes, umatId }
}

export interface OrangBaruKeluargaInput {
  keluargaId: string
  parokiId: string | null
  lingkunganId: string
  /** The person's columns, `nama_lengkap` among them: written over what a grid row gives. */
  kolom: Readonly<Record<string, unknown>>
  /** Their role (a `HUBUNGAN` code; never the head: the family has one). */
  hubungan: string
  /** The device's "today" in WIT, the membership's `mulai`. */
  hariIni: string
  newId?: (() => string) | undefined
}

/**
 * A new person joining a family that exists, one by one (Tambah umat; each new member of a
 * ceremony batch, spec 2026-10-02 upacara bersama): `tulisanAnggotaBaru`'s person and open
 * membership, the person carrying the columns given and both rows the role.
 */
export function tulisanOrangBaruKeluarga(input: OrangBaruKeluargaInput): {
  umat: LocalWrite
  anggota: LocalWrite
  umatId: string
} {
  const { writes, umatId } = tulisanAnggotaBaru({
    keluargaId: input.keluargaId,
    parokiId: input.parokiId,
    lingkunganId: input.lingkunganId,
    row: {
      ...barisKosong(),
      namaLengkap: String(input.kolom.nama_lengkap ?? ''),
      hubungan: labelHubungan(input.hubungan),
    },
    kepala: false,
    hariIni: input.hariIni,
    ...(input.newId === undefined ? {} : { newId: input.newId }),
  })
  const [umat, anggota] = writes
  if (umat === undefined || anggota === undefined)
    throw new Error('tulisanAnggotaBaru wrote no person')
  umat.fields = { ...umat.fields, ...input.kolom, hubungan: input.hubungan }
  anggota.fields = { ...anggota.fields, hubungan: input.hubungan }
  return { umat, anggota, umatId }
}

/**
 * One save's writes, one op per row (the later fields winning, the rows in their first order):
 * two shared steps saved together (Pendaftaran's step 11), or a step's own columns with what
 * a reading adds beyond them (Dokumen jadi teks). The ops handed in are not changed.
 */
export function gabungTulisan(writes: readonly LocalWrite[]): LocalWrite[] {
  const out: LocalWrite[] = []
  for (const w of writes) {
    const sama = out.find((o) => o.table === w.table && o.row_id === w.row_id)
    if (sama === undefined) out.push({ ...w, fields: { ...w.fields } })
    else sama.fields = { ...sama.fields, ...w.fields }
  }
  return out
}

export interface KeluargaBaruInput {
  rumah: RumahTangga
  /** The grid rows with a name; the first is the head of the family. */
  anggota: readonly BarisAnggota[]
  hariIni: string
  pendaftaran: Pendaftaran
  keluargaId?: string
  newId?: () => string
}

/** A family typed from its Kartu Keluarga: the family row, every member, the head link. */
export function tulisanKeluargaBaru(input: KeluargaBaruInput): {
  writes: LocalWrite[]
  keluargaId: string
  umatIds: string[]
} {
  const newId = input.newId ?? uuidv7
  const keluargaId = input.keluargaId ?? newId()
  const umatIds: string[] = []
  const anggotaWrites: LocalWrite[] = []
  input.anggota.forEach((row, i) => {
    const { writes, umatId } = tulisanAnggotaBaru({
      keluargaId,
      parokiId: input.rumah.parokiId,
      lingkunganId: input.rumah.lingkunganId,
      row,
      kepala: i === 0,
      hariIni: input.hariIni,
      newId,
    })
    umatIds.push(umatId)
    anggotaWrites.push(...writes)
  })
  const keluarga: LocalWrite = {
    table: 'keluarga',
    row_id: keluargaId,
    fields: {
      paroki_id: input.rumah.parokiId,
      lingkungan_id: input.rumah.lingkunganId,
      nomor_kk_katolik: input.rumah.nomorKkKatolik,
      nomor_kk_pemerintah: input.rumah.nomorKkPemerintah,
      kode_keluarga: input.rumah.kodeKeluarga ?? null,
      kepala_keluarga_id: umatIds[0] ?? null,
      alamat: input.rumah.alamat,
      rt_rw: input.rumah.rtRw,
      kampung_desa: input.rumah.kampung,
      kecamatan: input.rumah.distrik,
      kabupaten_kota: input.rumah.kabupatenKota ?? null,
      provinsi: input.rumah.provinsi ?? null,
      kode_pos: input.rumah.kodePos ?? null,
      tanggal_kk: input.rumah.tanggalKk ?? null,
      status_rumah: input.rumah.statusRumah ?? null,
      status: 'aktif',
      pendaftaran: input.pendaftaran,
      tanggal_mulai: input.hariIni,
    },
  }
  return { writes: [keluarga, ...anggotaWrites], keluargaId, umatIds }
}

/** The `keluarga.pendaftaran` write of a step. */
export function tulisanKeadaan(keluargaId: string, pendaftaran: Pendaftaran): LocalWrite {
  return { table: 'keluarga', row_id: keluargaId, fields: { pendaftaran } }
}

export const KUNCI_SAKRAMEN: Readonly<Record<TabelSakramen, KunciSakramen>> = {
  baptis: 'baptis',
  komuni_pertama: 'komuni',
  krisma: 'krisma',
  perkawinan: 'perkawinan',
}

/** The umat column that links to this parish's entry; a marriage links from the entry. */
export const KOLOM_TAUTAN: Readonly<
  Record<TabelSakramen, 'baptis_id' | 'komuni_id' | 'krisma_id' | null>
> = {
  baptis: 'baptis_id',
  komuni_pertama: 'komuni_id',
  krisma: 'krisma_id',
  perkawinan: null,
}

/** An outside reference's parish that names the reading parish's own book: "Paroki ini". */
export function adalahParokiIni(paroki: unknown): boolean {
  return typeof paroki === 'string' && /^paroki ini$/i.test(paroki.trim())
}

/**
 * This parish's own book, its entry not yet in SATU: the outside reference "Paroki ini" without a
 * book number, which "Sudah · Di paroki ini" writes when there is no entry to link (test sweep
 * 2026-10-04, A2). The entry added later (Tautkan, Tambah entri) replaces it.
 */
export function rujukanParokiIniTanpaEntri(
  ref: NonNullable<ReferensiSakramenLuar['baptis']> | null | undefined,
): boolean {
  if (ref === null || ref === undefined) return false
  const tanpaNomor = (v: unknown) => v === null || v === undefined || String(v).trim() === ''
  return adalahParokiIni(ref.paroki) && tanpaNomor(ref.buku) && tanpaNomor(ref.nomor)
}

export interface TautanInput {
  umatId: string
  tabel: TabelSakramen
  entriId: string
  /** The spouse slot a marriage entry gets; null links neither. */
  jenisKelamin: string | null
  /** The person's stored outside references, so the linked sacrament's is dropped. */
  referensiLuar: ReferensiSakramenLuar | null
}

/** Tautkan: the person points at the entry and the entry at the person, in one save. */
export function tulisanTautan(input: TautanInput): LocalWrite[] {
  const writes: LocalWrite[] = []
  const umat: Record<string, unknown> = {}
  const kolom = KOLOM_TAUTAN[input.tabel]
  if (kolom !== null) umat[kolom] = input.entriId
  const kunciLuar = KUNCI_SAKRAMEN[input.tabel]
  if (input.referensiLuar !== null && input.referensiLuar[kunciLuar] != null) {
    umat.referensi_sakramen_luar = { ...input.referensiLuar, [kunciLuar]: null }
  }
  if (input.tabel === 'perkawinan') {
    if (input.jenisKelamin === 'L') {
      writes.push({
        table: 'perkawinan',
        row_id: input.entriId,
        fields: { pria_umat_id: input.umatId },
      })
    } else if (input.jenisKelamin === 'P') {
      writes.push({
        table: 'perkawinan',
        row_id: input.entriId,
        fields: { wanita_umat_id: input.umatId },
      })
    }
  } else {
    writes.push({ table: input.tabel, row_id: input.entriId, fields: { umat_id: input.umatId } })
  }
  if (Object.keys(umat).length > 0)
    writes.unshift({ table: 'umat', row_id: input.umatId, fields: umat })
  return writes
}

export interface RujukanLuarInput {
  umatId: string
  tabel: TabelSakramen
  referensiLuar: ReferensiSakramenLuar | null
  rujukan: NonNullable<ReferensiSakramenLuar['baptis']>
}

/** The certificate of another parish, merged into `referensi_sakramen_luar`. */
export function tulisanRujukanLuar(input: RujukanLuarInput): LocalWrite {
  const kunciLuar = KUNCI_SAKRAMEN[input.tabel]
  const fields: Record<string, unknown> = {
    referensi_sakramen_luar: { ...(input.referensiLuar ?? {}), [kunciLuar]: input.rujukan },
  }
  // A reference from elsewhere replaces a link into this parish's book.
  const kolom = KOLOM_TAUTAN[input.tabel]
  if (kolom !== null) fields[kolom] = null
  return { table: 'umat', row_id: input.umatId, fields }
}

/** "menjahit, mengukir" to the stored tags; empty entries dropped, duplicates kept once. */
export function bacaKeterampilan(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of text.split(/[,;\n]/)) {
    const t = part.replace(/\s+/g, ' ').trim()
    if (t === '' || seen.has(t.toLowerCase())) continue
    seen.add(t.toLowerCase())
    out.push(t)
  }
  return out
}

// ---------------------------------------------------------------------------
// The blank Formulir Pendataan, from the same schema (printed by /cetak, section 11)
// ---------------------------------------------------------------------------

export interface BagianFormulir {
  readonly id: string
  readonly judul: string
  readonly jenis: 'isian' | 'tabel' | 'per-anggota'
  /** `isian`: the blanks of the block; `per-anggota`: the blocks repeated for each member. */
  readonly isian?: readonly IsianPendaftaran[]
  readonly kolom?: readonly KolomAnggota[]
  readonly blok?: readonly {
    readonly id: string
    readonly judul: string
    readonly isian: readonly IsianPendaftaran[]
  }[]
}

const tanpaPindai = (isian: readonly IsianPendaftaran[]) =>
  isian.filter((f) => f.jenis !== 'pindai')
const adaTidak = (id: string, label: string): IsianPendaftaran => ({
  id,
  label,
  jenis: 'radio',
  opsional: true,
  opsi: [
    { value: 'ada', label: teks.formulir.ada },
    { value: 'tidak_ada', label: teks.formulir.tidakAda },
  ],
})

const kolomUmat = ui.keluarga.umat.kolom
const langkahUmat = ui.langkahUmat

/**
 * The parish data sheet on paper (release 19): the certificate's number and church beside the six
 * fields of a reference. The screen's block asks them too (`BlokSakramen`).
 */
const ISIAN_RUJUKAN_KERTAS: readonly IsianPendaftaran[] = [
  ...ISIAN_RUJUKAN,
  { id: 'nomorSurat', label: langkahUmat.sakramen.nomorSurat, jenis: 'teks', lebar: 'setengah' },
  { id: 'gereja', label: langkahUmat.sakramen.gereja, jenis: 'teks', lebar: 'setengah' },
]

/**
 * The paper's sacrament table (release 19): the four sacraments of a member are one table, a row
 * each, these its columns in this order (the parish and the church first, the letter number
 * last), so a member's block keeps to one F4 page with the parish data sheet's fields. The fields
 * of `ISIAN_RUJUKAN_KERTAS`, reordered; every other field of a sacrament's block follows the table.
 */
export const KOLOM_SAKRAMEN_KERTAS: readonly IsianPendaftaran[] = [
  'paroki',
  'gereja',
  'buku',
  'halaman',
  'nomor',
  'tanggal',
  'tempat',
  'nomorSurat',
].flatMap((id) => ISIAN_RUJUKAN_KERTAS.filter((f) => f.id === id))

/**
 * Household block, the members table, then the per-member blocks in the poster's order. The parish
 * data sheet (release 19) adds the household's housing, each member's religion and birth order
 * first, the certificates' number and church and the kinds of baptism and marriage, the school now
 * attended and the profession. The sensitive fields are never on paper: a ketua collects it, and
 * the parish staff ask them at the desk.
 */
export function bagianFormulir(): readonly BagianFormulir[] {
  const step = (id: LangkahId) => BY_ID.get(id) as StepDef
  const kk = step('kk')
  return [
    { id: 'kk', judul: judulLangkah(kk), jenis: 'isian', isian: tanpaPindai(kk.isian) },
    { id: 'anggota', judul: teks.formulir.anggota, jenis: 'tabel', kolom: KOLOM_ANGGOTA_KK },
    {
      id: 'per-anggota',
      judul: teks.formulir.perAnggota,
      jenis: 'per-anggota',
      blok: [
        {
          id: 'diri',
          judul: langkahUmat.dataDiri.judul,
          isian: [
            {
              id: 'agama',
              label: kolomUmat.agama,
              jenis: 'radio',
              opsi: opsiDari(ui.enum.agama),
              lebar: 'penuh',
            },
            {
              id: 'agamaSebelumnya',
              label: kolomUmat.agamaSebelumnya,
              jenis: 'teks',
              lebar: 'pendek',
            },
            { id: 'gerejaAsal', label: kolomUmat.gerejaAsal, jenis: 'teks', lebar: 'setengah' },
            { id: 'anakKe', label: kolomUmat.anakKe, jenis: 'angka', lebar: 'pendek' },
          ],
        },
        {
          id: 'baptis',
          judul: step('baptis').label,
          isian: [
            ...ISIAN_RUJUKAN_KERTAS,
            {
              id: 'jenisBaptis',
              label: langkahUmat.sakramen.jenisBaptis,
              jenis: 'radio',
              opsi: opsiDari(ui.enum.jenisBaptis),
              lebar: 'penuh',
            },
          ],
        },
        { id: 'komuni', judul: step('komuni').label, isian: ISIAN_RUJUKAN_KERTAS },
        { id: 'krisma', judul: step('krisma').label, isian: ISIAN_RUJUKAN_KERTAS },
        {
          id: 'perkawinan',
          judul: step('perkawinan').label,
          isian: [
            ...ISIAN_RUJUKAN_KERTAS,
            {
              id: 'jenisPerkawinan',
              label: langkahUmat.sakramen.jenisPerkawinan,
              jenis: 'radio',
              opsi: opsiDari(ui.enum.jenisPerkawinanLuar),
              lebar: 'penuh',
            },
            ...step('perkawinan').isian.filter((f) => f.id === 'statusPerkawinan'),
            adaTidak('aktaNikahSipil', teks.sakramen.aktaNikahSipil),
          ],
        },
        {
          id: 'kontak',
          judul: `${step('hp').label} · ${step('email').label}`,
          isian: [...step('hp').isian, ...step('email').isian],
        },
        {
          id: 'pendidikan',
          judul: step('pendidikan').label,
          isian: [
            ...tanpaPindai(step('pendidikan').isian),
            {
              id: 'pendidikanSaatIni',
              label: kolomUmat.pendidikanSaatIni,
              jenis: 'teks',
              lebar: 'setengah',
            },
            { id: 'tahunLulus', label: kolomUmat.tahunLulus, jenis: 'angka', lebar: 'pendek' },
            { id: 'jurusan', label: kolomUmat.jurusan, jenis: 'teks', lebar: 'setengah' },
            { id: 'namaSekolah', label: kolomUmat.namaSekolah, jenis: 'teks', lebar: 'setengah' },
            adaTidak('ijazah', teks.pendidikan.ijazah),
          ],
        },
        {
          id: 'pekerjaan',
          judul: step('pekerjaan').label,
          isian: [
            ...step('pekerjaan').isian,
            { id: 'profesi', label: kolomUmat.profesi, jenis: 'teks', lebar: 'setengah' },
            {
              id: 'anggotaCu',
              label: kolomUmat.anggotaCu,
              jenis: 'radio',
              opsi: opsiDari(ui.enum.anggotaCu),
            },
          ],
        },
      ],
    },
  ]
}
